import styles from "./cd.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import {DoubleBar} from "story/components/graphs/bar"
import * as graphUtil from "shared/graphs/util"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, deposit, interest],
[year, deposit, interest],
[year, deposit, interest],
]
*/

const getTotal = (principal, months, rate, compounding) => {
  const yearsElapsed = Math.max(months / 12, 0.01)

  const n = compounding === "daily" ? 365 : compounding === "monthly" ? 12 : compounding === "quarterly" ? 4 : compounding === "annually" ? 1 : NaN
  // number of compounding periods per year

  return principal * Math.pow(1 + rate / n, n * yearsElapsed)
}

const getTotalInterest = (p, m, r, c) => getTotal(p, m, r, c) - p

const getGraphData = (principal, months, rate, compounding) =>
  util.xAxisArray(months).reduce((memo, month) => {
    const newInterest = util.round(getTotalInterest(principal, month, rate, compounding))

    return [...memo, [month, principal, newInterest]]
  }, [])

const calculate = ({principal, months, rate, compoundingType}) => {
  const data = getGraphData(principal, months, rate / 100, compoundingType)
  const interestEarned = data[data.length - 1][2]

  return {
    data,
    interestEarned,
    totalBalance: interestEarned + principal
  }
}

// NOTE: These exports are to support the Share Certificate calculator, which is basically a clone of the
// CD calculator, just with minor adjustments to labels.
// Any changes made to these exports will also affect that calculator.
export const toActivityFactory =
  (calcId, calcTitle) =>
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {compoundingType, principal, months, rate, resetKey} = state
    const {id, theme} = getProps()

    const {data, interestEarned, totalBalance} = calculate(state)

    const updateCompoundingType = (type) => updateState("compoundingType")(type)

    const accessibilityTableHeadings = [
      {name: "Months", key: "month"},
      {name: "Interest Earned", key: "interest"}
    ]

    const accessibilityTableData = data.map(([month, deposit, interest]) => ({
      month,
      interest: util.formatDollarsNoCents(interest)
    }))

    const accessibilityTableLabel = "CD Calculator table"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.ChartContainer>
              <DoubleBar
                isStacked={true}
                values={data}
                yAxis="Balance"
                xAxis="Months"
                formatter="currency"
                tooltip={[
                  "Month",
                  {name: "Initial Deposit", type: "currency", color: graphUtil.getColor(theme, 2)},
                  {name: "Interest Earned", type: "currency", color: graphUtil.getColor(theme, 4)}
                ]}
                customTooltipStyles={{width: 281}}
                type="calculator"
                colors={[graphUtil.getColor(theme, 2), graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
                ariaLabel={`This bar graph visually represents how much you can save in a ${calcTitle}. Read below to find the total balance and interest earned over time.`}
              />
            </shared.ChartContainer>
            <shared.SummaryContainer>
              <callouts.Callout>
                <callouts.Detail label="Total Balance" result={util.formatDollarsNoCents(totalBalance)} />
                <callouts.Detail label="Interest Earned" result={util.formatDollarsNoCents(interestEarned)} />
              </callouts.Callout>
            </shared.SummaryContainer>
            <shared.InputsContainer className={styles["inputs"]}>
              <div>
                <inputs.Label inputId={`bz-${calcId}-calc-compounding-select`}>Compounding</inputs.Label>
                <inputs.Dropdown
                  id={`bz-${calcId}-calc-compounding`}
                  options={[
                    ["daily", "Daily"],
                    ["monthly", "Monthly"],
                    ["quarterly", "Quarterly"],
                    ["annually", "Annually"]
                  ]}
                  initialValue={compoundingType}
                  onChange={updateCompoundingType}
                ></inputs.Dropdown>
              </div>
              <inputs.CalcInput id={id} title="Initial Deposit" onChange={updateState("principal", util.fromString)} initialValue={principal} resetKey={resetKey} />
              <inputs.Input id={id} title="Interest Rate" onChange={updateState("rate")} value={rate} valueType="percent" />
              <inputs.RangeInput
                id={id}
                title="Months to Save"
                onChange={updateState("months", util.fromString)}
                initialValue={months}
                min={1}
                max={60}
                step={1}
                units="months"
                valueType="months"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  }

export const stateFilter = (state) => {
  const {data, interestEarned, totalBalance} = calculate(state)
  return Object.assign(state, {
    totalBalance,
    interestEarned
  })
}

export const defaultState = {
  compoundingType: "monthly",
  principal: 10000,
  months: 60,
  rate: 2.5
}

export default shared.ActivityFactory(toActivityFactory("cd", "CD"), {
  stateFilter,
  defaultProps: {
    id: "cd-calculator",
    className: "cd-calculator",
    title: "CD Calculator",
    subtitle: "Adjust the inputs to see how much your savings in a CD grow over time."
  },
  defaultState
})
